import axios from "axios";
import { getMockCardBySlug } from "../mocks/cardsBySlug.mock.ts";
import { mockCardsResponse } from "../mocks/cards.mock.ts";

const apiClient = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Required to send session cookies cross-origin
});

// Optional: Enable mocks for local development
// Set VITE_USE_MOCKS=true in your .env file to activate
if (import.meta.env.VITE_USE_MOCKS === "true") {
  console.log("🔧 API Mocks are ENABLED");

  // ---------------------------------------------------------------------------
  // Mock rules — add a new entry here to mock additional endpoints.
  // Each rule is matched in order; the first match wins.
  // ---------------------------------------------------------------------------
  type MockRule = {
    method: string;
    pattern: RegExp;
    handler: (matches: RegExpMatchArray) => unknown;
  };

  const mockRules: MockRule[] = [
    {
      method: "get",
      pattern: /^\/cards\/by-slug\/([^/]+)\/([^/]+)$/,
      handler: ([, categorySlug, cardSlug]) =>
        getMockCardBySlug(categorySlug!, cardSlug!),
    },
    {
      method: "get",
      pattern: /^\/cards$/,
      handler: () => mockCardsResponse,
    },
    {
      method: "post",
      pattern: /^\/checkout\/session$/,
      handler: () => ({
        url: "/checkout?status=success&mockCheckout=1",
      }),
    },
    {
      method: "get",
      pattern: /^\/checkout\/session\/([^/]+)$/,
      handler: ([, sessionId]) => ({
        sessionId,
        paymentStatus: "paid",
        paymentAmountCents: 699,
        paymentIntentId: `mock_pi_${sessionId}`,
        customerEmail: null,
      }),
    },
    {
      method: "post",
      pattern: /^\/orders$/,
      handler: () => ({
        order: {
          id: Date.now(),
          status: "unpaid",
        },
      }),
    },
    {
      method: "post",
      pattern: /^\/cards\/uploadInsideImage$/,
      handler: () => ({
        success: true,
        url: `/uploads/generated/mock_inside_${Date.now()}.png`,
      }),
    },
    {
      method: "get",
      pattern: /^\/orders$/,
      handler: () => {
        try {
          const mockOrders = window.localStorage.getItem("mockOrdersResponse");
          if (mockOrders) {
            return JSON.parse(mockOrders);
          }
        } catch {}

        return {
          orders: [],
        };
      },
    },
    {
      method: "get",
      pattern: /^\/card-present\/([^/]+)\/([^/]+)$/,
      handler: ([, cartItemId]) => ({
        cartItem: {
          id: cartItemId,
          title: "Golden Hour Anniversary",
          subtitle: "A little note opened in full",
          frontImageUrl: "/cardify-about-us-1.jpg",
          insideImageUrl: "/cardify-about-us-2.jpg",
          paperName: "Matte 240 GSM",
        },
      }),
    },
    {
      method: "get",
      pattern: /^\/orders\/([^/]+)$/,
      handler: ([, id]) => ({
        order: {
          id: Number(id),
          payment_id: `mock_pi_${id}`,
          payment_amount_cents: 699,
          status: "unpaid",
          created_at: new Date().toISOString(),
          cart_items: [],
        },
      }),
    },
    {
      method: "put",
      pattern: /^\/orders\/([^/]+)\/status$/,
      handler: ([, id]) => ({
        order: {
          id: Number(id),
          payment_id: `mock_pi_${id}`,
          payment_amount_cents: 699,
          status: "printed",
          created_at: new Date().toISOString(),
          cart_items: [],
        },
      }),
    },
    {
      method: "get",
      pattern: /^\/server-health$/,
      handler: () => ({
        diskUsedPercent: 62.4,
        diskRemainingGb: 118.7,
        ramUsedPercent: 54.1,
        ramRemainingGb: 7.3,
        averageCpuUsagePercent: 24.8,
        sslCertificateValidTo: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        sslCertificateDaysRemaining: 5,
        sslCertificateIsExpiringSoon: true,
      }),
    },
    {
      method: "post",
      pattern: /^\/contact-us$/,
      handler: () => ({
        message: "Contact request sent.",
      }),
    },
    {
      method: "post",
      pattern: /^\/forgot-password$/,
      handler: () => ({
        message: "If an account with this email exists, a password reset email has been sent.",
      }),
    },
    {
      method: "post",
      pattern: /^\/login\/google$/,
      handler: () => ({
        message: "Login successful!",
        token: "eyJhbGciOiJIUzI1NiJ9.eyJleHAiOjQxMDI0NDQ4MDB9.signature",
        userId: 404,
        email: "google-user@example.com",
        isAdmin: false,
        isCreator: false,
      }),
    },
    {
      method: "post",
      pattern: /^\/reset-password$/,
      handler: () => ({
        message: "Password reset successfully. You can now log in.",
      }),
    },
    {
      method: "get",
      pattern: /^\/email-templates$/,
      handler: () => ({
        templates: [
          {
            id: 1,
            template_key: "verification",
            subject: "Verify Your Email Address - Cardify.it",
            text_content:
              "Verify Your Email - Cardify.it\n\nThank you for registering at Cardify.it!\n\nPlease verify your email address by clicking the following link:\n{{verification_url}}\n\nIf you did not create an account with Cardify.it, please ignore this email.",
            html_content:
              "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\"><title>Verify Your Email - Cardify.it</title></head><body style=\"font-family: Arial, sans-serif; line-height: 1.6; max-width: 600px; margin: 0 auto; padding: 20px;\"><div style=\"background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;\"><h1 style=\"color: white; margin: 0;\">Cardify.it</h1></div><div style=\"background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px;\"><h2 style=\"color: #333;\">Verify Your Email Address</h2><p style=\"color: #666;\">Thank you for registering at Cardify.it! Please click the button below to verify your email address and activate your account.</p><div style=\"text-align: center; margin: 30px 0;\"><a href=\"{{verification_url}}\" style=\"background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;\">Verify Email</a></div><p style=\"color: #666; font-size: 14px;\">Or copy and paste this link into your browser:</p><p style=\"color: #667eea; word-break: break-all; font-size: 14px;\">{{verification_url}}</p></div></body></html>",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          {
            id: 2,
            template_key: "order_confirmation",
            subject: "Your Cardify.it order confirmation",
            text_content:
              "Your Cardify.it order is in\n\nThank you for your order. We have it and we're already getting it ready.\n\nView your order here:\n{{order_url}}\n\nWe appreciate you choosing Cardify.it to send something thoughtful.",
            html_content:
              "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\"><title>Your Cardify.it Order</title></head><body style=\"font-family: Arial, sans-serif; line-height: 1.6; max-width: 600px; margin: 0 auto; padding: 20px;\"><div style=\"background: linear-gradient(135deg, #111827 0%, #374151 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;\"><h1 style=\"color: white; margin: 0;\">Cardify.it</h1></div><div style=\"background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;\"><h2 style=\"color: #111827;\">Your order is in</h2><p style=\"color: #4b5563;\">Thank you for your order. We have it and we're already getting it ready.</p><p style=\"color: #4b5563;\">You can view the full order details, including your card previews, at the link below.</p><div style=\"text-align: center; margin: 30px 0;\"><a href=\"{{order_url}}\" style=\"background: #111827; color: white; padding: 15px 30px; text-decoration: none; border-radius: 999px; display: inline-block; font-weight: bold;\">View your order</a></div><p style=\"color: #6b7280;\">We appreciate you choosing Cardify.it to send something thoughtful.</p><p style=\"color: #6b7280;\">Order link: <a href=\"{{order_url}}\" style=\"color: #2563eb;\">{{order_url}}</a></p></div></body></html>",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          {
            id: 3,
            template_key: "password_reset",
            subject: "Reset your Cardify.it password",
            text_content:
              "Reset your Cardify.it password\n\nWe received a request to reset your password.\n\nUse the link below to choose a new password:\n{{reset_url}}\n\nIf you did not request this, you can ignore this email.",
            html_content:
              "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\"><title>Reset your Cardify.it password</title></head><body style=\"font-family: Arial, sans-serif; line-height: 1.6; max-width: 600px; margin: 0 auto; padding: 20px;\"><div style=\"background: linear-gradient(135deg, #0f172a 0%, #334155 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;\"><h1 style=\"color: white; margin: 0;\">Cardify.it</h1></div><div style=\"background: #f8fafc; padding: 30px; border-radius: 0 0 10px 10px;\"><h2 style=\"color: #0f172a;\">Reset your password</h2><p style=\"color: #475569;\">We received a request to reset your password. Use the button below to choose a new one.</p><div style=\"text-align: center; margin: 30px 0;\"><a href=\"{{reset_url}}\" style=\"background: #0f172a; color: white; padding: 15px 30px; text-decoration: none; border-radius: 999px; display: inline-block; font-weight: bold;\">Reset password</a></div><p style=\"color: #64748b; font-size: 14px;\">If the button does not work, use this link:</p><p style=\"color: #2563eb; word-break: break-all; font-size: 14px;\">{{reset_url}}</p></div></body></html>",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
      }),
    },
    {
      method: "put",
      pattern: /^\/email-templates\/([^/]+)$/,
      handler: ([, templateKey]) => ({
        template: {
          id: Date.now(),
          template_key: templateKey,
          subject: "Preview subject",
          text_content: "Preview text",
          html_content: "<html><body><p>Preview HTML</p></body></html>",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      }),
    },
  ];

  apiClient.interceptors.request.use((config) => {
    const method = config.method?.toLowerCase() ?? "";

    for (const rule of mockRules) {
      if (method !== rule.method) continue;
      const matches = config.url?.match(rule.pattern);
      if (!matches) continue;

      console.log(`[Mock] Intercepting ${method.toUpperCase()} ${config.url}`);

      throw {
        __isMock: true,
        data: rule.handler(matches),
        status: 200,
        config,
      };
    }

    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      // Catch the special placeholder and return it as a successful response
      if (error && error.__isMock) {
        return Promise.resolve({
          data: error.data,
          status: error.status,
          statusText: "OK",
          headers: {},
          config: error.config,
        });
      }
      return Promise.reject(error);
    },
  );
}

export default apiClient;
